import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import { selectControls } from '../../../redux/controls';
import { RosterExpandable, RosterModalProps, RosterClose, StudentInfo, RosterDelete } from '../models/Modal.interfaces';
import './RostersModal.scss';
import { cloneDeep } from 'lodash';
import Spinner from '../../Spinner/Spinner';
import { DeletionType, onClickExpandAsync } from '../models';
import RosterGridItem from '../../RosterGridItem/RosterGridItem';
import Modal from '../Modal';
import { deleteRosterStart, deleteRosterStudentStart, fetchRostersStart, resetRosters, selectRosterList, selectRostersErrMsg, selectRostersLoading } from '../../../redux/rosters';
import { selectIsLoggedIn } from '../../../redux/account';
import gaEvents from '../../../shared/analyticsEvents';

function RosterModal({ open, setOpen, onClose }: RosterModalProps): JSX.Element {

  const dispatch = useDispatch();

  const controls = useSelector(selectControls);
  const rosterList = useSelector(selectRosterList);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const loading = useSelector(selectRostersLoading);
  const errMsg = useSelector(selectRostersErrMsg);

  ReactModal.setAppElement('#root');

  //modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [deletionType, setDeletionType] = useState<DeletionType>('DELETE_ROSTER');
  const [deletionObject, setDeletionObject] = useState<RosterDelete|null>(null);
  const [modalBodyText, setModalBody] = useState<string>('');

  const deleteRoster = useCallback((
    item: RosterExpandable,
  ) => {
    dispatch(
      deleteRosterStart(item)
    );
  }, [dispatch]);

  const deleteStudent = useCallback(async(
    studInfo: StudentInfo
  ) => {
    dispatch(
      deleteRosterStudentStart(studInfo)
    );
  }, [dispatch]);

  const onClickEdit = useCallback((
    item: RosterExpandable
  ) => {
    onClose({create: false, edit: true, rosterToEdit: item});
  }, [onClose]);

  const onClickDeleteRoster = useCallback((item: RosterExpandable) => {
    setDeletionObject({item});
    setDeletionType('DELETE_ROSTER');
    setModalBody('This action will delete the selected roster and the test admin data associated with it. Do you want to continue?');
    setIsModalOpen(true);
    setOpen(false);
  }, [setOpen]);

  const onClickDeleteStudent = useCallback((item: RosterExpandable, studInfo: StudentInfo) => {
    setDeletionObject({item, studInfo});
    setDeletionType('DELETE_STUDENTS');
    setModalBody('This action will delete the selected student in this roster and the test admin data associated with it. Do you want to continue?');
    setIsModalOpen(true);
    setOpen(false);
  }, [setOpen]);

  const onModalClose = useCallback((confirmDelete: boolean) => {
    if (confirmDelete) {
      if (deletionType === 'DELETE_ROSTER' && deletionObject) {
        deleteRoster(deletionObject.item);
        gaEvents.onGA4DeleteRoster();
      } else if (deletionType === 'DELETE_STUDENTS' && deletionObject?.studInfo) {
        deleteStudent(deletionObject.studInfo);
        gaEvents.onGA4DeleteStudentFromRoster();
      }
    } else {
      setDeletionObject(null);
    }
    setIsModalOpen(false);
    setOpen(true);
  }, [deleteRoster, deleteStudent, deletionObject, deletionType, setOpen] );

  // fetch Rosters or reset Roster state if logged out
  useEffect(() => {
    if (isLoggedIn) {
      dispatch(
        fetchRostersStart()
      );
    } else {
      dispatch(
        resetRosters()
      );
    }
    return () => {
      dispatch(
        resetRosters()
      );
    };
  }, [dispatch, isLoggedIn]);

  return (
    <>
      {
        isModalOpen &&
        <Modal
          headerText='Delete Confirmation'
          bodyText={modalBodyText}
          open={isModalOpen}
          onClose={onModalClose}
          windowType='PROMPT'
        />
      }
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal nqt-roster-modal"
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          shouldReturnFocusAfterClose={true}
          contentLabel={controls?.LoginPUManageRosters?.controlText}
        >
          <div className='nqt-modal--container nqt-roster-modal--container'>
            {
              (loading) && <Spinner/>
            }
            <div className="nqt-modal--header-group">
              <header className='nqt-modal--title'>{controls?.LoginPUManageRosters?.controlText}</header>
              <button className='nqt-modal--close-button'
                ref={(el) => el?.focus()}
                onClick={() => {onClose({} as RosterClose);}}>
                <DeleteIcon/>
                <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
              </button>
            </div>
            <div className="nqt-modal--border">
              <section className="nqt-modal--content container-fluid">
                <div className="row row-cols-1 gy-2">
                  <div className="col nqt-roster-modal--description">
                    {
                      controls?.RosterManageInst?.controlText
                    }
                  </div>
                  <div className="col nqt-modal-roster--button-container">
                    <button className="nqt-button--submit nqt-modal-roster--button"
                      onClick={() =>{onClose({create: true, edit:false}); gaEvents.onGA4CreateRoster(); }}>
                      <FontAwesomeIcon className='fa-plus' icon={faPlus}></FontAwesomeIcon>
                      <span className='nqt-modal-roster--button__text'>
                        {
                          controls?.RosterCreateRoster?.controlText
                        }
                      </span>
                    </button>
                  </div>
                  {
                    errMsg &&
                    <div className="roster-modal error-message-container">
                      <span className="error-message">
                        {
                          errMsg
                        }
                      </span>
                    </div>
                  }
                </div>
              </section>
              {
                !!rosterList?.length &&
                <section className="nqt-modal--content container-fluid nqt-roster-modal--list-container px-0">
                  <div className="row row-cols-1 gy-1 mx-0">
                    <div className="col nqt-roster-modal--list-headers">
                      <header className="nqt-roster-modal--list-header">{controls?.RosterActions?.controlText}</header>
                      <header className="nqt-roster-modal--list-header">{controls?.RosterName?.controlText}</header>
                    </div>
                    <ul className="col nqt-roster-modal--list">
                      {
                        rosterList.map((item:RosterExpandable) => (
                          <RosterGridItem
                            key={item.rosterID}
                            isExpanded={!!item.isExpanded}
                            onDelete={() => onClickDeleteRoster(item)}
                            onEdit={() => onClickEdit(item)}
                            onExpand={() =>
                              onClickExpandAsync(cloneDeep(item), dispatch)
                            }
                            roster={item}>
                            {
                              item.children &&
                              !!item.children.length &&
                              item.children.map(studInfo => (
                                <li key={studInfo.studentID} className="nqt-roster-grid-item--student-list-item">
                                  <button className="nqt-roster-grid-item--delete nqt-roster-modal--link"
                                    disabled={item?.children?.length === 1}
                                    onClick={() => onClickDeleteStudent(item, studInfo)}>
                                    {
                                      controls?.RosterDelete?.controlText
                                    }
                                  </button>
                                  <button className="nqt-roster-grid-item--student-name nqt-roster-modal--link">
                                    {studInfo.studentName}
                                  </button>
                                </li>
                              ))
                            }
                          </RosterGridItem>
                        ))
                      }
                    </ul>
                  </div>
                </section>
              }
            </div>
          </div>
        </ReactModal>
      }
    </>
  );
}

export default RosterModal;